import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Megaphone, Clock, ArrowRight, AlertTriangle } from 'lucide-react'
import { ConfirmSendDialog } from './ConfirmSendDialog'
import { RecipientField } from './RecipientField'
import { UnreachableNotice } from './UnreachableNotice'
import { TemplatePicker } from './TemplatePicker'
import { useRecipientChips, type Chip } from './useRecipientChips'
import { createBroadcast } from '@/utils/broadcastQueryOptions'
import type { BroadcastPriority } from '@/types/broadcastTypes'
import { useShellStore } from '@/stores/shellStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { insertTemplateBody, senderPrefix, unfilledPlaceholders, withSenderPrefix } from '@/lib/format'
import { cn } from '@/lib/utils'

// At this many recipients a normal send also asks for confirmation. Urgent sends
// always do.
const CONFIRM_FROM = 25

function broadcastLabel(chips: Chip[]): string {
  if (chips.length === 0) return 'Broadcast'
  if (chips.length === 1) return chips[0].name
  return `${chips[0].name} +${chips.length - 1}`
}

export function Composer() {
  const isMobile = useIsMobile()
  const backToList = useShellStore((s) => s.backToList)
  const selectConversation = useShellStore((s) => s.selectConversation)
  const queryClient = useQueryClient()

  const field = useRecipientChips()
  const [message, setMessage] = useState('')
  const [priority, setPriority] = useState<BroadcastPriority>('normal')
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)

  const {
    chips,
    recipients,
    total,
    textable,
    unreachable,
    groupsResolving,
    groupCount,
    hasChips,
    recipientLine,
  } = field

  const createMutation = useMutation({
    mutationFn: () =>
      createBroadcast({
        name: broadcastLabel(chips),
        message: message.trim(),
        priority,
        recipients: recipients.map((r) => ({
          phoneNumber: r.phoneNumber,
          displayName: r.displayName,
          azureAdUserId: r.azureAdUserId,
        })),
      }),
    onSuccess: (detail) => {
      queryClient.setQueryData(['broadcast', detail.id], detail)
      queryClient.invalidateQueries({ queryKey: ['broadcasts'] })
      field.clear()
      setMessage('')
      setPriority('normal')
      setError(null)
      selectConversation(detail.id, 'broadcast')
    },
    onError: (e: Error) => setError(e.message),
  })

  const unfilled = unfilledPlaceholders(message)
  const canSend =
    textable > 0 &&
    message.trim().length > 0 &&
    unfilled.length === 0 &&
    !groupsResolving &&
    !createMutation.isPending
  const urgent = priority === 'urgent'

  const send = () => {
    if (!canSend) return
    if (urgent || textable >= CONFIRM_FROM) setConfirming(true)
    else createMutation.mutate()
  }

  const sendLabel = urgent ? `Send urgent to ${textable}` : `Send to ${textable}`

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header — megaphone + indigo marks this as the one-way broadcast screen. */}
      <div className="flex min-h-[56px] flex-none items-center gap-2.5 border-b border-border px-[18px] py-[9px]">
        {isMobile && (
          <button
            type="button"
            onClick={backToList}
            className="p-1 text-muted-foreground"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-primary-tint text-primary">
          <Megaphone className="h-[17px] w-[17px]" />
        </span>
        <div className="min-w-0">
          <div className="text-base font-extrabold leading-tight">New broadcast</div>
          <div className="mt-px truncate text-[11.5px] text-muted-foreground">
            Each person gets their own text · replies come back to you only
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-[22px]">
        <div className="mx-auto flex w-full max-w-[640px] flex-col gap-4">
          <RecipientField field={field} label="To" placeholder="Add groups or people…" />

          {/* Recipient summary */}
          {hasChips && (
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-[7px] rounded-full bg-primary-tint px-[13px] py-1.5 text-[13px] font-bold text-primary">
                <Megaphone className="h-[13px] w-[13px]" />
                {recipientLine}
                {groupsResolving && <span className="font-medium opacity-70">· resolving…</span>}
              </span>
              {groupCount > 1 && (
                <span className="text-[12px] text-muted-foreground">
                  duplicates removed automatically
                </span>
              )}
              <UnreachableNotice unreachable={unreachable} />
            </div>
          )}

          {/* Priority */}
          <div>
            <div className="mb-[7px] text-[12px] font-bold uppercase tracking-[0.03em] text-muted-foreground">
              Priority
            </div>
            <div className="inline-flex gap-[3px] rounded-[10px] border border-border bg-surface-2 p-[3px]">
              <button
                type="button"
                onClick={() => setPriority('normal')}
                className={cn(
                  'rounded-lg px-3.5 py-[7px] text-[12.5px] font-bold transition-colors',
                  !urgent ? 'bg-surface text-foreground shadow-wha-sm' : 'text-muted-foreground',
                )}
              >
                Normal
              </button>
              <button
                type="button"
                onClick={() => setPriority('urgent')}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-lg px-3.5 py-[7px] text-[12.5px] font-bold transition-colors',
                  urgent ? 'bg-destructive text-white' : 'text-muted-foreground',
                )}
              >
                <span
                  className={cn('h-2 w-2 rounded-full', urgent ? 'bg-white' : 'bg-destructive')}
                />
                Urgent
              </button>
            </div>
          </div>

          {/* Message */}
          <div>
            <div className="mb-[7px] flex items-center justify-between gap-2">
              <span className="text-[12px] font-bold uppercase tracking-[0.03em] text-muted-foreground">
                Message
              </span>
              <TemplatePicker
                onSelect={(t) => {
                  setMessage((prev) => insertTemplateBody(prev, t.body))
                  setPriority(t.priority)
                }}
              />
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message… (recipients can reply)"
              className="min-h-[150px] w-full resize-y rounded-xl border-[1.5px] border-border bg-surface p-[13px] text-[14.5px] leading-[1.6] text-foreground outline-none"
            />
            <div className="mt-1.5 flex justify-between gap-3 text-[12px] text-muted-foreground">
              <span>{withSenderPrefix(message, urgent).length} characters</span>
              <span>Starts with “{senderPrefix(urgent).trim()}” so staff know it's from us</span>
            </div>
            {unfilled.length > 0 && (
              <div className="mt-1.5 text-[12.5px] font-semibold text-danger-ink">
                Fill in {unfilled.join(', ')} before sending
              </div>
            )}
          </div>

          {/* Amber double-check */}
          {hasChips && (
            <div className="flex items-start gap-2 rounded-[10px] border border-warn-border bg-warn-tint px-3.5 py-[11px] text-[13px] font-semibold text-warn-ink">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                Double-check — this reaches <b>{recipientLine}</b>
                {total !== textable && (
                  <> · {total - textable} skipped with no mobile on file</>
                )}
              </span>
            </div>
          )}

          {error && (
            <div className="rounded-[10px] bg-destructive/10 px-3.5 py-3 text-[13px] text-destructive">
              {error}
            </div>
          )}

          {/* Footer actions */}
          <div className="flex flex-wrap justify-end gap-2.5">
            <button
              type="button"
              disabled
              title="Scheduling coming soon"
              className="inline-flex items-center gap-1.5 rounded-[11px] border-[1.5px] border-border bg-surface px-4 py-3 text-sm font-semibold text-foreground opacity-60"
            >
              <Clock className="h-4 w-4" />
              Schedule
            </button>
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              className={cn(
                'inline-flex items-center gap-2 rounded-[11px] px-5 py-3 text-[14.5px] font-extrabold transition-colors',
                !canSend
                  ? 'cursor-not-allowed bg-border text-faint'
                  : urgent
                    ? 'bg-destructive text-white'
                    : 'bg-primary text-on-primary',
              )}
            >
              {urgent && canSend && <span className="h-2 w-2 rounded-full bg-white" />}
              {createMutation.isPending ? 'Sending…' : sendLabel}
              {!createMutation.isPending && <ArrowRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <ConfirmSendDialog
        open={confirming}
        onOpenChange={setConfirming}
        count={textable}
        message={message.trim()}
        urgent={urgent}
        title={urgent ? 'Send this urgent broadcast?' : 'Send this broadcast?'}
        onConfirm={() => createMutation.mutate()}
      />
    </div>
  )
}
